import React, { useEffect, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import type { AiTestSourceOfTruthOption } from './types';
import { listTesterSourceOfTruth } from './api';

interface SourceOfTruthPickerProps {
  projectId: number | '';
  value: AiTestSourceOfTruthOption | null;
  onChange: (option: AiTestSourceOfTruthOption | null) => void;
  disabled?: boolean;
}

const SourceOfTruthPicker: React.FC<SourceOfTruthPickerProps> = ({
  projectId,
  value,
  onChange,
  disabled,
}) => {
  const [options, setOptions] = useState<AiTestSourceOfTruthOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const valueRef = useRef(value);
  valueRef.current = value;

  useEffect(() => {
    if (!projectId) {
      setOptions([]);
      onChangeRef.current(null);
      return;
    }
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const rows = await listTesterSourceOfTruth(projectId);
        if (cancelled) return;
        setOptions(rows);
        const current = valueRef.current;
        const stillValid = current && rows.some((row) => row.key === current.key);
        if (!stillValid) {
          onChangeRef.current(rows[0] || null);
        }
      } catch (err) {
        if (!cancelled) {
          setOptions([]);
          onChangeRef.current(null);
          setError(err instanceof Error ? err.message : 'Could not load source of truth files');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  return (
    <div>
      <label className="block text-[12px] font-medium text-[#626262] mb-1">
        Source of truth file
      </label>
      <select
        value={value?.key || ''}
        onChange={(e) => {
          const next = options.find((row) => row.key === e.target.value) || null;
          onChange(next);
        }}
        disabled={disabled || loading || !projectId}
        className="w-full rounded-md border border-[#D1D1D1] px-3 py-2 text-sm"
      >
        <option value="">{projectId ? 'Select a source of truth file' : 'Select a project first'}</option>
        {options.map((row) => (
          <option key={row.key} value={row.key}>
            {row.label}
          </option>
        ))}
      </select>
      {loading && (
        <div className="mt-1 flex items-center gap-2 text-[12px] text-[#8A8A8A]">
          <Loader2 className="w-3 h-3 animate-spin" /> Loading reports…
        </div>
      )}
      {error && <p className="mt-1 text-[12px] text-red-600">{error}</p>}
      {!loading && projectId && options.length === 0 && !error && (
        <p className="mt-1 text-[12px] text-[#8A8A8A]">
          No completed source of truth reports for this project yet.
        </p>
      )}
    </div>
  );
};

export default SourceOfTruthPicker;
